import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import {
    noticeError,
    useModal,
    trackHeapEvent,
    getReferrerURL,
    getAffiliateData,
    GiftOfferIncentiveModal,
    AFFILIATE_QUERY_PARAM_KEYS,
    GIFT_OFFER_LEAD_QUERY_PARAMS,
    LightboxFullScreenTakeoverStyles,
} from '@ratehub/base-ui';

import trackHeapCCWebClickEvent from '../functions/trackHeapCCWebClickEvent';
import trackHeapCCRedirectEvent from '../functions/trackHeapCCRedirectEvent';
import navigateNewTabToMoreDetails from '../functions/navigateNewTabToMoreDetails';
import isCardEligibleForReferralOffer from '../functions/isCardEligibleForReferralOffer';


/**
 * Creates a callback for applying for a credit card.
 * When the card features a gift card offer it will open the gift card modal before proceeding with normal processing.
 * @param {object} [options] - options
 * @param {boolean} [options.trackHotJarSurvey=false] - should we trigger a HotJar survey event?
 * @returns {(function({
 *      cardInfo: {CreditCardShape},
 *      [onApplyNow: onApplyNowCallback],
 *      [applyURLOverride: string],
 *      [provinceCode: string],
 *      [enableRedirect: boolean],
 *  })
 *      : (null|undefined))|*}
 */
function useApplyForCreditCardCallback({ trackHotJarSurvey = false } = {}) {
    const modal = useModal();

    return useCallback(
        /**
         * To be invoked when a user is applying for a credit card so that it will open the gift card modal when needed.
         * @param {CreditCardShape} cardInfo
         * @param {GiftOfferShape} [cardInfo.giftCardOffer]
         * @param {onApplyNowCallback} [onApplyNow]
         * @param {string} [applyURLOverride]
         * @param {string} [provinceCode]
         * @param {boolean} [enableRedirect]
         * @param {string} [componentName]
         * @returns {null}
         */
        ({
            cardInfo,
            onApplyNow,
            applyURLOverride,
            provinceCode,
            enableRedirect = true,
            componentName,
            isCreditReportGenerated = false,
            cardIndex,
        }) => {
            if (!cardInfo) {
                noticeError(new RangeError('`cardInfo` argument to credit card callback must not be empty.'));

                return null;
            }

            trackHeapCCWebClickEvent(cardInfo, trackHotJarSurvey);

            const {
                isMonetized,
                applyRedirectURL,
                detailsURL,
                isSponsored,
                bank,
                giftCardOffer,
                hasReferralOffer,
            } = cardInfo;


            const hasGiftCardOffer = !!giftCardOffer;
            const isReferralEligible = isCardEligibleForReferralOffer(hasReferralOffer);

            // do the standard onApplyNow() action when there is no gift card offer and no referral eligibility,
            // navigate if enableRedirect, and then we are done; no modal will open
            if (!hasGiftCardOffer && !isReferralEligible) {
                trackHeapEvent('CC conversion attempt', {
                    href: detailsURL,
                    provider: bank,
                    monetized: isMonetized.toString(),
                    sponsored: isSponsored.toString(),
                });

                trackHeapCCRedirectEvent({
                    referrerURL: getReferrerURL(),
                    affiliateId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId],
                    cardInfo,
                });

                onApplyNow?.(cardInfo);
                if (enableRedirect) {
                    navigateNewTabToMoreDetails({
                        isMonetized,
                        detailsURL,
                        applyRedirectURL,
                        applyRedirectURLOverride: applyURLOverride,
                        queryParams: {
                            ...(provinceCode && { province: provinceCode }),
                            sponsored: isSponsored,
                            ...(componentName && { component_name: componentName }),
                        },
                    });
                }

                return null;
            }

            // If we did not exit early with the return directly above:
            //  • there IS a gift card offer OR a referral offer, AND
            //  • we have been asked to show it (by context or by experiment)
            // If this is invoked, we know they explicitly accepted or declined the offer.
            // They did NOT cancel, and we are about to send them to the provider’s site.
            function handleAcceptOrDecline(additionalInfo) {
                modal.close(modalId);

                const { email, firstName, lastName, isOfferAccepted } = additionalInfo;

                trackHeapCCRedirectEvent({
                    referrerURL: getReferrerURL(),
                    affiliateId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId],
                    additionalInfo,
                    cardInfo,
                    isCreditReportGenerated,
                    cardIndex,
                });

                onApplyNow?.(cardInfo, additionalInfo);
                if (enableRedirect) {
                    navigateNewTabToMoreDetails({
                        isMonetized,
                        detailsURL,
                        applyRedirectURL,
                        applyRedirectURLOverride: applyURLOverride,
                        queryParams: {
                            ...(provinceCode && { province: provinceCode }),
                            ...(isOfferAccepted && {
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.email]: email,
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.firstName]: firstName,
                                // make sure to send an empty string to confirm intentional blankness
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.lastName]: lastName ?? '',
                                [GIFT_OFFER_LEAD_QUERY_PARAMS.isGiftCardLead]: isOfferAccepted,
                            }),
                            sponsored: isSponsored,
                            ...(componentName && { component_name: componentName }),
                        },
                    });
                }
            }

            // We use the modal’s layer id to close the modal programmatically
            const modalId = modal.open({
                isContentFullWidth: true,
                showBranding: false,
                verticalAlignment: 'flex-start',

                style: LightboxFullScreenTakeoverStyles,

                // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
                screenReaderLabel: hasGiftCardOffer
                    ? (
                        <FormattedMessage
                            id="edb.cc.gift-card.screen-reader"
                            defaultMessage="Credit card gift offer"
                        />
                    )
                    : (
                        <FormattedMessage
                            id="edb.cc.referral-offer.screen-reader"
                            defaultMessage="Referral offer bonus"
                        />
                    ),

                children: (
                    <GiftOfferIncentiveModal
                        giftOffer={cardInfo.giftCardOffer}
                        title={cardInfo.name}
                        providerName={cardInfo.providerName}
                        hasNewsletterOffer={true}
                        onAcceptOrDecline={handleAcceptOrDecline}
                        isReferralEligible={isReferralEligible}
                        referralOfferValue={cardInfo.referralOfferValue}
                    />
                ),
            });
        },
        [ modal ],
    );
}


/**
 * @callback onApplyNowCallback
 * @param {CreditCardShape} cardInfo - the selected card
 * @param {boolean} [isOfferAccepted=false] - has the user accepted the offer?
 */

export default useApplyForCreditCardCallback;
